import { Component, OnInit, Inject } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialog,
} from '@angular/material/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { Constants } from 'src/app/app.constants';
import { VoucherService } from 'src/app/services/voucher.service';
import { MerchantService } from 'src/app/services';
import { Observable, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
  map,
  catchError,
} from 'rxjs/operators';
import {
  UserVoucher,
  UserVoucherRedemption,
} from 'src/app/models/user_voucher';
import { TwoOptionAlertComponent } from 'src/app/shared/components/two-option-alert/two-option-alert.component';
import { NoopScrollStrategy } from '@angular/cdk/overlay';
import { Customer } from 'src/app/models/customer';
import { User } from 'src/app/models/user';

@Component({
  selector: 'app-redeem-voucher-dialog',
  templateUrl: './redeem-voucher-dialog.component.html',
  styleUrls: ['./redeem-voucher-dialog.component.css'],
})
export class RedeemVoucherDialogComponent implements OnInit {
  codeFormControl = new FormControl('');
  submitted = false;
  searching = false;
  redeeming = false;
  sendingEmail = false;
  downloadingPDF = false;
  voucherDetails: any = null;
  filteredVouchers: Observable<any[]>;
  private searchSubject = new Subject<string>();
  isPartialRedemption = false;
  redemptionAmountControl = new FormControl<number | null>(null, [
    Validators.required,
    Validators.min(0),
    Validators.max(99999),
  ]);

  // Customer assignment properties
  customers: Customer[] = [];
  customersLoading = false;
  assignedCustomers: (User | any)[] = [];
  customerSearchControl = new FormControl<Customer | null>(null);
  assigningCustomer = false;

  // Expiry date editing properties
  editingExpiry = false;
  newExpiryDate: Date | null = null;
  updatingExpiry = false;

  constructor(
    public matDialogRef: MatDialogRef<RedeemVoucherDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private voucherService: VoucherService,
    public constants: Constants,
    private merchantService: MerchantService,
    private dialog: MatDialog,
  ) {
    if (data?.voucherDetails) {
      this.voucherDetails = data.voucherDetails;
    }
    this.filteredVouchers = this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((searchTerm) => {
        if (!searchTerm || searchTerm.length < 2) {
          return [];
        }
        this.searching = true;
        return this.voucherService.searchVouchers(searchTerm, 10).pipe(
          map((response) => {
            this.searching = false;
            return response.docs;
          }),
          catchError((error) => {
            this.searching = false;
            this.constants.snack(
              error.error?.message || 'Failed to search vouchers',
            );
            return [];
          }),
        );
      }),
    );
    this.redemptionAmountControl.valueChanges.subscribe(() => {
      this.onRedemptionAmountChange();
    });
  }

  ngOnInit(): void {
    if (!this.voucherDetails) {
      this.codeFormControl.valueChanges.subscribe((value) => {
        if (value && typeof value === 'string') {
          this.searchSubject.next(value.toUpperCase());
        }
      });
    } else {
      this.redemptionAmountControl.setValue(this.maxRedemptionAmount);
    }
  }

  onTabChange(event: any): void {
    // Tab index 2 is the "Joint Holders" tab (0: Details, 1: Activity, 2: Joint Holders)
    if (event.index === 2 && !this.customers.length) {
      this.loadCustomers();
    }
  }

  onOptionSelected(voucher: any): void {
    this.voucherDetails = voucher;
    this.codeFormControl.setValue(voucher.code, { emitEvent: false });
  }

  onInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.toUpperCase();
  }

  searchVoucher(): void {
    this.submitted = true;
    if (!this.codeFormControl.valid) return;

    this.searching = true;
    this.voucherService
      .getVoucherByCode(this.codeFormControl.value!?.toUpperCase())
      .subscribe({
        next: (res: any) => {
          this.voucherDetails = res;
        },
        error: (error: HttpErrorResponse) => {
          this.constants.snack(error.error.message || 'Voucher not found');
        },
      })
      .add(() => (this.searching = false));
  }

  onPartialRedemptionChange(): void {
    if (!this.isPartialRedemption) {
      this.redemptionAmountControl.setValue(this.voucherDetails.value);
    } else {
      this.redemptionAmountControl.setValue(null);
    }
  }

  onRedemptionAmountChange(): void {
    const amount = this.redemptionAmountControl.value;
    if (amount !== null && amount > this.maxRedemptionAmount) {
      this.redemptionAmountControl.setValue(this.maxRedemptionAmount);
    }
  }

  getRemainingBalance(): number {
    const redeemAmount = this.redemptionAmountControl.value ?? 0;
    return this.maxRedemptionAmount - redeemAmount;
  }

  redeemVoucher(): void {
    const currentDate = new Date();
    const expiryDate = new Date(this.voucherDetails.expiry);

    if (this.voucherDetails.status === 'redeemed') {
      this.constants.snack('This voucher has already been redeemed');
      return;
    }

    if (
      this.voucherDetails.expiry &&
      (this.voucherDetails.status === 'expired' || expiryDate < currentDate)
    ) {
      this.constants.snack('This voucher has expired');
      return;
    }

    if (
      this.isPartialRedemption &&
      (this.redemptionAmountControl.invalid ||
        this.redemptionAmountControl.value === null)
    ) {
      this.constants.snack('Please enter a valid redemption amount');
      return;
    }

    const amount = this.isPartialRedemption
      ? this.redemptionAmountControl.value!
      : this.maxRedemptionAmount;

    this.showRedemptionConfirmation(amount);
  }

  private showRedemptionConfirmation(amount: number): void {
    const voucherTitle = this.voucherDetails.voucher?.title || 'voucher';
    const currency = this.voucherDetails.voucher?.currency || '£';
    const isPartial = this.isPartialRedemption;

    const confirmationMessage = isPartial
      ? `Are you sure you want to redeem <b>${currency}${amount}</b> from this ${voucherTitle}?`
      : `Are you sure you want to redeem this ${voucherTitle} for <b>${currency}${amount}</b>?`;

    const dialogRef = this.dialog.open(TwoOptionAlertComponent, {
      data: {
        title: 'Confirm Redemption',
        body: confirmationMessage,
        buttonOne: 'Cancel',
        buttonTwo: 'Redeem',
      },
      scrollStrategy: new NoopScrollStrategy(),
      maxWidth: '400px',
      autoFocus: false,
      disableClose: true,
      panelClass: 'custom-dialog',
    });

    dialogRef.afterClosed().subscribe((option: number) => {
      if (option === 1) {
        this.processRedemption(amount);
      }
    });
  }

  private processRedemption(amount: number): void {
    this.redeeming = true;
    this.merchantService
      .redeemVoucher(this.voucherDetails._id, amount)
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.constants.snack('Voucher redeemed successfully');
            this.matDialogRef.close(true);
          }
        },
        error: (error: HttpErrorResponse) => {
          this.constants.snack(
            error.error.message || 'Failed to redeem voucher',
          );
        },
      })
      .add(() => (this.redeeming = false));
  }

  resetSearch(): void {
    this.voucherDetails = null;
    this.codeFormControl.reset();
    this.submitted = false;
  }

  sendVoucherEmail(): void {
    if (!this.voucherDetails?.code) {
      this.constants.snack('No voucher code available');
      return;
    }

    const recipientEmail =
      this.voucherDetails?.recipient?.email || this.voucherDetails?.user?.email;
    const recipientName =
      this.voucherDetails?.recipient?.name || this.voucherDetails?.user?.name;

    const confirmationMessage = `Send voucher email to ${recipientName}${
      recipientEmail ? ` (${recipientEmail})` : ''
    }?`;

    const dialogRef = this.dialog.open(TwoOptionAlertComponent, {
      data: {
        title: 'Confirm',
        body: confirmationMessage,
        buttonOne: 'Cancel',
        buttonTwo: 'Send',
      },
      scrollStrategy: new NoopScrollStrategy(),
      autoFocus: false,
      width: '350px',
      disableClose: true,
      panelClass: 'custom-dialog',
    });

    dialogRef.afterClosed().subscribe((option: number) => {
      if (option === 1) {
        this.performSendEmail();
      }
    });
  }

  private performSendEmail(): void {
    this.sendingEmail = true;
    this.voucherService
      .sendVoucherEmail(this.voucherDetails.code)
      .subscribe({
        next: (res: any) => {
          this.constants.snack('Email sent successfully');
        },
        error: (error: HttpErrorResponse) => {
          this.constants.snack(error.error?.message || 'Failed to send email');
        },
      })
      .add(() => (this.sendingEmail = false));
  }

  getVoucherStatus(): string {
    if (this.voucherDetails.status === 'redeemed') return 'Redeemed';
    if (this.voucherDetails.status === 'cancelled') return 'Cancelled';
    if (this.voucherDetails.status === 'partial') return 'Partial Redeemed';
    if (this.voucherDetails.status === 'active') return 'Active';

    if (this.voucherDetails.expiry) {
      const currentDate = new Date();
      const expiryDate = new Date(this.voucherDetails.expiry);
      if (expiryDate < currentDate) return 'Expired';
    }

    return 'Pending';
  }

  getPurchaserName(): string {
    if (this.voucherDetails?.source == 'merchant_issued') {
      return 'Merchant';
    }

    if (
      this.voucherDetails?.purchaser &&
      this.voucherDetails?.user &&
      this.voucherDetails?.purchaser === this.voucherDetails?.user?._id
    ) {
      return `${this.voucherDetails?.user?.name} (${this.voucherDetails?.user?.email})`;
    }

    if (this.voucherDetails?.customer?.name) {
      return `${this.voucherDetails?.customer?.name}`;
    }

    if (this.voucherDetails?.other?.name) {
      return `${this.voucherDetails?.other?.name} (${this.voucherDetails?.other?.email})`;
    }

    return 'App user (non-customer)';
  }

  get validStatus() {
    return (
      this.voucherDetails?.status === 'active' ||
      this.voucherDetails?.status === 'partial'
    );
  }

  statusLabel(voucher: UserVoucher) {
    //Cannot be redeemed
    if (voucher?.status === 'pending') return 'Pending'; //cannot be used yet
    if (voucher?.status === 'expired') return 'Expired'; //expired
    if (voucher?.status === 'redeemed') return 'Redeemed'; //fully redeemed
    if (voucher?.status === 'cancelled') return 'Cancelled'; //cancelled

    //Can be redeemed
    if (voucher?.status === 'active') return 'Active'; //can be used, not yet redeemed
    if (voucher?.status === 'partial') return 'Partial Redeemed'; //partially redeemed

    return 'Unknown';
  }

  get fullRedemption(): UserVoucherRedemption {
    return this.voucherDetails?.redemptions?.find(
      (el: any) => el.type === 'full',
    );
  }

  get maxRedemptionAmount(): number {
    return this.voucherDetails?.remainingValue ?? this.voucherDetails?.value;
  }

  downloadVoucherPDF(): void {
    if (!this.voucherDetails?._id) {
      this.constants.snack(
        'Unable to download PDF - voucher details not available',
      );
      return;
    }

    this.downloadingPDF = true;
    this.voucherService.downloadVoucherPDF(this.voucherDetails._id).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `voucher-${this.voucherDetails.code}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      },
      error: (error) => {
        this.constants.snack(
          'Error downloading voucher PDF. Please try again.',
        );
        console.error('Error downloading voucher PDF:', error);
      },
      complete: () => {
        this.downloadingPDF = false;
      },
    });
  }

  get hasRecipientEmail(): boolean {
    return !!(
      this.voucherDetails?.recipient?.email ||
      (this.voucherDetails?.source === 'merchant_issued' &&
        this.voucherDetails?.user?.email)
    );
  }

  loadCustomers(): void {
    this.customersLoading = true;
    this.merchantService
      .getCustomers('_id', 'desc', 5000, 0, '', {})
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.customers = res.docs;
            this.customers.forEach((el) => {
              if (typeof el.user !== 'string') {
                el.name = el.user?.name ?? el?.name;
                el.email = el.user?.email ?? el?.email;
              }
            });
            this.customers.sort((a, b) => a.name!.localeCompare(b.name!));
            this.loadAssignedCustomers();
          }
        },
        error: (res: HttpErrorResponse) => {
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.customersLoading = false));
  }

  loadAssignedCustomers(): void {
    if (!this.voucherDetails?.assignedUsers) {
      this.assignedCustomers = [];
      return;
    }

    this.assignedCustomers = this.voucherDetails.assignedUsers
      .map((userId: string | User) => {
        const id = typeof userId === 'string' ? userId : userId._id;
        return this.customers.find(
          (c) => (typeof c.user === 'string' ? c.user : c.user?._id) === id,
        );
      })
      .filter((c: any) => c !== undefined);
  }

  assignCustomer(customer: Customer): void {
    if (!customer._id || !this.voucherDetails._id) {
      return;
    }

    const dialogRef = this.dialog.open(TwoOptionAlertComponent, {
      data: {
        title: 'Assign Customer',
        body: `Assign ${customer.name} to this voucher? They will be able to see and use this voucher in their mobile app.`,
        buttonOne: 'Cancel',
        buttonTwo: 'Assign',
        hasToggle: true,
        toggleLabel: 'Notify this customer via push notification?',
        toggleDefaultValue: true,
      },
      scrollStrategy: new NoopScrollStrategy(),
      autoFocus: false,
      width: '400px',
      disableClose: true,
      panelClass: 'custom-dialog',
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result?.option === 1 || result === 1) {
        const notify =
          result?.toggleValue !== undefined ? result.toggleValue : true;
        this.performAssignCustomer(customer, notify);
      }
    });
  }

  performAssignCustomer(customer: Customer, notify: boolean = true): void {
    this.assigningCustomer = true;
    this.merchantService
      .assignCustomerToVoucher(this.voucherDetails._id, customer._id!, notify)
      .subscribe({
        next: (res: any) => {
          this.constants.snack('Customer assigned successfully');
          this.voucherDetails = res;
          this.loadAssignedCustomers();
        },
        error: (error: HttpErrorResponse) => {
          this.constants.snack(
            error.error.message || 'Failed to assign customer',
          );
        },
      })
      .add(() => (this.assigningCustomer = false));
  }

  unassignCustomer(customer: Customer): void {
    if (!customer._id || !this.voucherDetails._id) {
      return;
    }

    const dialogRef = this.dialog.open(TwoOptionAlertComponent, {
      data: {
        title: 'Unassign Customer',
        body: `Remove ${customer.name} from this voucher? They will no longer be able to see or use this voucher.`,
        buttonOne: 'Cancel',
        buttonTwo: 'Remove',
      },
      scrollStrategy: new NoopScrollStrategy(),
      autoFocus: false,
      width: '400px',
      disableClose: true,
      panelClass: 'custom-dialog',
    });

    dialogRef.afterClosed().subscribe((option: number) => {
      if (option === 1) {
        this.performUnassignCustomer(customer);
      }
    });
  }

  performUnassignCustomer(customer: Customer): void {
    this.assigningCustomer = true;
    this.merchantService
      .unassignCustomerFromVoucher(this.voucherDetails._id, customer._id!)
      .subscribe({
        next: (res: any) => {
          this.constants.snack('Customer removed successfully');
          this.voucherDetails = res;
          this.loadAssignedCustomers();
        },
        error: (error: HttpErrorResponse) => {
          this.constants.snack(
            error.error.message || 'Failed to remove customer',
          );
        },
      })
      .add(() => (this.assigningCustomer = false));
  }

  get availableCustomers(): Customer[] {
    if (!this.customers.length) {
      return [];
    }

    const assignedUserIds = new Set(
      (this.voucherDetails?.assignedUsers || []).map((u: any) => {
        const id = typeof u === 'string' ? u : u._id;
        return id?.toString();
      }),
    );

    // Also exclude the primary user
    const primaryUserId = (
      typeof this.voucherDetails?.user === 'string'
        ? this.voucherDetails.user
        : this.voucherDetails?.user?._id
    )?.toString();

    return this.customers.filter((c) => {
      const userId = (
        typeof c.user === 'string' ? c.user : c.user?._id
      )?.toString();
      return userId && !assignedUserIds.has(userId) && userId !== primaryUserId;
    });
  }

  startEditingExpiry(): void {
    this.editingExpiry = true;
    this.newExpiryDate = this.voucherDetails.expiry
      ? new Date(this.voucherDetails.expiry)
      : null;
  }

  cancelEditingExpiry(): void {
    this.editingExpiry = false;
    this.newExpiryDate = null;
  }

  getExpiryDateValue(): string {
    if (!this.newExpiryDate) {
      return '';
    }
    const date = new Date(this.newExpiryDate);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  getMinDate(): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  onExpiryDateChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.value) {
      this.newExpiryDate = new Date(input.value);
    } else {
      this.newExpiryDate = null;
    }
  }

  saveExpiryDate(): void {
    if (this.updatingExpiry) {
      return;
    }

    this.updatingExpiry = true;
    this.merchantService
      .updateVoucherExpiry(this.voucherDetails._id, this.newExpiryDate)
      .subscribe({
        next: (res: any) => {
          this.voucherDetails.expiry = res.expiry;
          this.voucherDetails.status = res.status;
          this.editingExpiry = false;
          this.newExpiryDate = null;
          this.constants.snack('Expiry date updated successfully');
        },
        error: (error: HttpErrorResponse) => {
          this.constants.snack(
            error.error.message || 'Failed to update expiry date',
          );
        },
      })
      .add(() => (this.updatingExpiry = false));
  }
}
